import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SEARCH_PAGE_MAP } from '../../app/routes/searchPageMap';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import { telemetryClient } from './telemetryClient';

const PAGE_ANALYTICS = Object.freeze({
  home: { screenName: 'home', navigationSection: 'home' },
  diagnostics: { screenName: 'diagnostics', navigationSection: 'diagnostics' },
  catalogHistory: { screenName: 'search_catalog_history', navigationSection: 'data' },
  dataSettings: { screenName: 'search_data_settings', navigationSection: 'data', settingsSection: 'data' },
  settingsV2: { screenName: 'search_settings_v2', navigationSection: 'settings', settingsSection: 'v2' },
  theme: { screenName: 'search_settings_theme', navigationSection: 'settings', settingsSection: 'theme' },
  searchBoxSettings: { screenName: 'search_settings_searchbox', navigationSection: 'settings', settingsSection: 'searchbox' },
  resultsPageSettings: { screenName: 'search_settings_result_page', navigationSection: 'settings', settingsSection: 'result_page' },
  searchMode: { screenName: 'search_settings_search_mode', navigationSection: 'settings', settingsSection: 'search_mode' },
  customizationFields: { screenName: 'search_customization_fields', navigationSection: 'customization', settingsSection: 'fields' },
  customizationTaxonomy: { screenName: 'search_customization_taxonomy', navigationSection: 'customization', settingsSection: 'taxonomy' },
  customizationEmptyQuery: { screenName: 'search_customization_empty_query', navigationSection: 'customization', settingsSection: 'empty_query' },
  cardTemplate: { screenName: 'search_customization_card_template', navigationSection: 'customization', settingsSection: 'card_template' },
  analyticHealth: { screenName: 'search_analytics_health', navigationSection: 'analytics', reportName: 'health' },
  analyticQueryPopular: { screenName: 'search_analytics_query_popular', navigationSection: 'analytics', reportName: 'query_popular' },
  analyticQueryNoResult: { screenName: 'search_analytics_query_no_result', navigationSection: 'analytics', reportName: 'query_no_result' },
  analyticLostOpportunities: { screenName: 'search_analytics_lost_opportunities', navigationSection: 'analytics', reportName: 'lost_opportunities' },
  analyticPopularProducts: { screenName: 'search_analytics_popular_products', navigationSection: 'analytics', reportName: 'popular_products' },
});

export function normalizeAdminRoute(pathname) {
  const segments = String(pathname || '').split('/').filter(Boolean);
  if (segments[0] !== 'app' || segments.length < 3) {
    return { screenName: 'other_admin', navigationSection: 'other' };
  }

  if (segments[2] === 'setup') return { screenName: 'setup_wizard', navigationSection: 'setup' };

  const pagePath = `/${segments.slice(2).join('/')}`;
  const page = SEARCH_PAGE_MAP.find(entry => (
    entry.surface.kind !== 'redirect' && entry.path === pagePath
  ));
  if (page && PAGE_ANALYTICS[page.id]) return PAGE_ANALYTICS[page.id];
  if (/^\/search\/analytic\/query\//.test(pagePath)) {
    return { screenName: 'search_query_detail', navigationSection: 'analytics', reportName: 'query_detail' };
  }
  if (segments[2] === 'search') return { screenName: 'search_other', navigationSection: 'search' };
  return { screenName: 'other_admin', navigationSection: 'other' };
}

export function TelemetryRouteTracker() {
  const location = useLocation();
  const { telemetryPolicy, telemetryPolicyResolved } = useShellAppContext();
  const previousSectionRef = useRef(null);

  useEffect(() => {
    if (!telemetryPolicyResolved || telemetryPolicy?.mode !== 'demo_full') {
      previousSectionRef.current = null;
      return;
    }

    const normalized = normalizeAdminRoute(location.pathname);
    telemetryClient.capture('demo_page_viewed', {
      screen_name: normalized.screenName,
      navigation_section: normalized.navigationSection,
    });
    if (previousSectionRef.current !== normalized.navigationSection) {
      telemetryClient.capture('demo_navigation_selected', {
        navigation_section: normalized.navigationSection,
        screen_name: normalized.screenName,
      });
      previousSectionRef.current = normalized.navigationSection;
    }
    if (normalized.reportName) {
      telemetryClient.capture('analytics_report_viewed', {
        report_name: normalized.reportName,
        screen_name: normalized.screenName,
      });
    }
    if (normalized.settingsSection) {
      telemetryClient.capture('settings_section_viewed', {
        settings_section: normalized.settingsSection,
        screen_name: normalized.screenName,
      });
    }
  }, [
    location.pathname,
    telemetryPolicy?.demoSessionId,
    telemetryPolicy?.mode,
    telemetryPolicy?.policyVersion,
    telemetryPolicyResolved,
  ]);

  return null;
}
